import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { getVenue } from "@/api/venues";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import ClientForm from "@/pages/admin/clients/ClientForm";
import ClientTermsAndAgreements from "@/pages/admin/clients/ClientTermsAndAgreements";
import { Button } from "@/components/ui/button";
import { enableClientLogin, disableClientLogin, setClientAsSigner, unsetClientAsSigner } from "@/api/clients.jsx";
import { FileSignature } from "lucide-react";

export default function VenueShow() {
    const { id } = useParams();
    const [venue, setVenue] = useState(null);
    const [modalOpen, setModalOpen] = useState(false);
    const [termsModalOpen, setTermsModalOpen] = useState(false);
    const [editingClient, setEditingClient] = useState(null); // Track if editing

    const fetchVenue = async () => {
        const data = await getVenue(id);
        if (data) setVenue(data);
    };

    const toggleLogin = async (client) => {
        const ftn = client.loginEnabled ? disableClientLogin : enableClientLogin;

        try {
            const updatedClient = await ftn(client.id);

            setVenue((prevVenue) => ({
                ...prevVenue,
                clients: prevVenue.clients.map((c) =>
                    c.id === updatedClient.id ? updatedClient : c
                ),
            }));
        } catch (error) {
            console.error("Failed to toggle login:", error);
        }
    };

    const setSigner = async (client) => {
        // Can only set a new signer, not unset
        if (client.isSigner) {
            return; // Already the signer, do nothing
        }

        try {
            const updatedClient = await setClientAsSigner(client.id);

            // Update state locally without refetching entire venue
            setVenue((prevVenue) => ({
                ...prevVenue,
                clients: prevVenue.clients.map((c) => {
                    // If this is the client being set as signer, use the updated data
                    if (c.id === updatedClient.id) {
                        return updatedClient;
                    }
                    // Unset all other signers
                    if (c.isSigner) {
                        return { ...c, isSigner: false };
                    }
                    return c;
                }),
            }));
        } catch (error) {
            console.error("Failed to set signer:", error);
        }
    };

    const getSignerClient = () => {
        return venue?.clients?.find(c => c.isSigner);
    };

    useEffect(() => {
        fetchVenue();
    }, [id]);

    if (!venue) return <div className="p-6">Loading...</div>;

    const signerClient = getSignerClient();

    return (
        <div className="p-6 max-w-6xl mx-auto">
            <div className="bg-white rounded-xl shadow p-6 space-y-6">
                <div className="flex justify-between items-center">
                    <h1 className="text-2xl font-bold text-[#5D1F42]">{venue.name}</h1>
                    <Button
                        onClick={() => {
                            setEditingClient(null); // reset for create mode
                            setModalOpen(true);
                        }}
                    >
                        Add Contact
                    </Button>
                </div>

                <div style={{ whiteSpace: "pre-line" }}>
                    <p className="text-gray-600">{venue.address || "No address provided"}</p>
                </div>

                <h2 className="text-xl font-semibold text-[#5D1F42] mt-6">Contacts</h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                    {venue.clients?.length > 0 ? (
                        venue.clients.map((client) => (
                            <div
                                key={client.id}
                                className="border rounded-lg p-4 shadow-sm bg-gray-50 space-y-2"
                            >
                                <div className="flex items-center justify-between">
                                    <p className="font-semibold text-[#5D1F42]">
                                        {client.firstName} {client.lastName}
                                    </p>
                                    {client.isSigner && (
                                        <Badge className="bg-[#5D1F42] text-white">
                                            <FileSignature className="w-3 h-3 mr-1" />
                                            Signer
                                        </Badge>
                                    )}
                                </div>
                                <p className="text-sm text-gray-700">{client.jobTitle}</p>
                                <p className="text-sm text-gray-500 break-words whitespace-pre-wrap">{client.tel}</p>
                                <p className="text-sm text-blue-600 underline break-words whitespace-pre-wrap">{client.email}</p>

                                <div className="flex flex-col gap-2 mt-2">
                                    <div className="flex gap-2">
                                        <Button
                                            size="sm"
                                            variant={client.loginEnabled ? "outline" : "default"}
                                            onClick={() => toggleLogin(client)}
                                        >
                                            {client.loginEnabled ? "Disable Login" : "Enable Login"}
                                        </Button>
                                        <Button
                                            size="sm"
                                            variant="outline"
                                            onClick={() => {
                                                setEditingClient(client);
                                                setModalOpen(true);
                                            }}
                                        >
                                            Edit
                                        </Button>
                                    </div>
                                    {!client.isSigner && (
                                        <Button
                                            size="sm"
                                            variant="secondary"
                                            className="w-full"
                                            onClick={() => setSigner(client)}
                                        >
                                            Set as Signer
                                        </Button>
                                    )}
                                </div>
                            </div>
                        ))
                    ) : (
                        <p className="text-sm text-gray-500 italic">No contacts found</p>
                    )}
                </div>

                {/* Terms & Agreements Section */}
                <div className="mt-8 border-t pt-6">
                    <div className="flex items-center justify-between mb-4">
                        <h2 className="text-xl font-semibold text-[#5D1F42] flex items-center gap-2">
                            <FileSignature className="w-5 h-5" />
                            Terms & Agreements
                        </h2>
                        {signerClient && (
                            <Badge variant="outline" className="text-sm">
                                Signer: {signerClient.firstName} {signerClient.lastName}
                            </Badge>
                        )}
                    </div>

                    {signerClient ? (
                        <ClientTermsAndAgreements
                            venueId={venue.id}
                            venueName={venue.name}
                        />
                    ) : (
                        <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 text-center">
                            <FileSignature className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                            <p className="text-gray-600 font-medium mb-2">No Signer Designated</p>
                            <p className="text-sm text-gray-500">
                                Please designate a contact as the signer to manage terms and agreements for this venue.
                            </p>
                        </div>
                    )}
                </div>
            </div>

            <Dialog open={modalOpen} onOpenChange={setModalOpen}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>
                            {editingClient ? "Edit Contact" : "Add New Contact"}
                        </DialogTitle>
                    </DialogHeader>

                    <ClientForm
                        venueId={venue.id}
                        client={editingClient}
                        onSave={() => {
                            setModalOpen(false);
                            setEditingClient(null);
                            fetchVenue();
                        }}
                    />
                </DialogContent>
            </Dialog>
        </div>
    );
}
